import React, { useEffect, useState, useCallback, useRef } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api } from '../../services/api'

// Phase 2 admin queue editor at /admin/initiatives/:slug/queue.
//
// The page is admin-only — non-admins get a 403 from the backend, and the
// route is rendered only via AdminProtectedRoute (see App.jsx).
//
// Reorder is implemented as up/down buttons in v1. Drag-and-drop is on the
// Phase 2 polish list but not blocking.

interface QueuePrompt {
  entry_id: number
  position: number
  prompt: {
    id: number
    question_text: string
    tags: string[] | null
  }
}

interface ActiveActivation {
  id: number
  status: string
  activated_at: string
  prompt: {
    id: number
    question_text: string
  }
}

interface QueueState {
  initiative: {
    id: number
    slug: string
    name: string
    allow_edits: boolean
  }
  // Several prompts can be live at once (2026-07); members step through
  // them in queue order.
  active_activations: ActiveActivation[]
  active_activation: ActiveActivation | null // legacy: first of the above
  queue: QueuePrompt[]
}

interface PromptResult {
  id: number
  question_text: string
  tags: string[] | null
  follow_up_instructions: string | null
}

export default function InitiativeQueue() {
  const { slug } = useParams<{ slug: string }>()
  const [state, setState] = useState<QueueState | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [acting, setActing] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<PromptResult[]>([])
  // Native HTML5 drag-and-drop reorder state: index of the row being dragged,
  // and the index it's currently hovering over (for the drop-indicator).
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [overIndex, setOverIndex] = useState<number | null>(null)

  // Only show the full-page loading state on the FIRST load. Post-action
  // refreshes (activate, reorder, remove) keep the current queue rendered —
  // the whole page used to swap to "Loading queue…" on every drag-drop.
  const hasLoadedRef = useRef(false)

  const refresh = useCallback(async () => {
    if (!slug) return
    if (!hasLoadedRef.current) setLoading(true)
    setError(null)
    try {
      const data: QueueState = await api.get(`/api/v1/admin/initiatives/${slug}/queue`)
      setState(data)
      hasLoadedRef.current = true
    } catch (e: any) {
      setError(e?.message || 'Failed to load queue')
    } finally {
      setLoading(false)
    }
  }, [slug])

  useEffect(() => {
    refresh()
  }, [refresh])

  // Search the prompt catalog (excluding already-queued prompts).
  useEffect(() => {
    if (!slug || query.trim().length < 2) {
      setResults([])
      return
    }

    let cancelled = false
    const t = setTimeout(async () => {
      try {
        const data: PromptResult[] = await api.get(
          `/api/v1/admin/prompts?q=${encodeURIComponent(query)}&exclude_initiative_slug=${slug}`,
        )
        if (!cancelled) setResults(data)
      } catch {
        if (!cancelled) setResults([])
      }
    }, 200)

    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [query, slug])

  async function runAction(thunk: () => Promise<unknown>) {
    setActing(true)
    setActionError(null)
    try {
      await thunk()
      await refresh()
    } catch (e: any) {
      setActionError(e?.message || 'Action failed')
    } finally {
      setActing(false)
    }
  }

  async function activateNext() {
    await runAction(() => api.post(`/api/v1/admin/initiatives/${slug}/activations`, {}))
  }

  async function finalizeActive(activationId: number) {
    await runAction(() =>
      api.patch(`/api/v1/admin/initiatives/${slug}/activations/${activationId}`, {
        transition: 'finalize',
      }),
    )
  }

  async function skipActive(activationId: number) {
    await runAction(() =>
      api.patch(`/api/v1/admin/initiatives/${slug}/activations/${activationId}`, {
        transition: 'skip',
      }),
    )
  }

  async function addPrompt(promptId: number) {
    await runAction(() =>
      api.post(`/api/v1/admin/initiatives/${slug}/queue`, { prompt_id: promptId }),
    )
    setQuery('')
    setResults([])
  }

  async function setAllowEdits(next: boolean) {
    await runAction(() =>
      api.patch(`/api/v1/admin/initiatives/${slug}/settings`, { allow_edits: next }),
    )
  }

  async function removePrompt(promptId: number) {
    await runAction(() =>
      api.delete(`/api/v1/admin/initiatives/${slug}/queue/${promptId}`),
    )
  }

  // Swap-with-neighbor reorder, used by the up/down buttons (kept as the
  // keyboard-accessible fallback alongside drag-and-drop).
  async function move(index: number, delta: number) {
    if (!state) return
    const queue = state.queue
    const target = index + delta
    if (target < 0 || target >= queue.length) return
    const ordered = queue.map((q) => q.prompt.id)
    ;[ordered[index], ordered[target]] = [ordered[target], ordered[index]]
    await runAction(() =>
      api.patch(`/api/v1/admin/initiatives/${slug}/queue`, { ordered_prompt_ids: ordered }),
    )
  }

  // Move an item from one index to another (drag-and-drop drop handler). Same
  // PATCH contract as `move` — the server validates the set is unchanged.
  async function reorder(fromIndex: number, toIndex: number) {
    if (!state) return
    if (fromIndex === toIndex || toIndex < 0 || toIndex >= state.queue.length) return
    const ordered = state.queue.map((q) => q.prompt.id)
    const [moved] = ordered.splice(fromIndex, 1)
    ordered.splice(toIndex, 0, moved)
    await runAction(() =>
      api.patch(`/api/v1/admin/initiatives/${slug}/queue`, { ordered_prompt_ids: ordered }),
    )
  }

  if (loading) return <div className="text-sm text-slate-500">Loading queue…</div>

  if (error) {
    return (
      <div className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
        {error}
      </div>
    )
  }

  if (!state) return null

  return (
    <div className="max-w-4xl space-y-8">
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500">{state.initiative.name}</div>
        <div className="mt-1 flex items-baseline justify-between gap-3">
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Prompt queue</h2>
          <Link
            to={`/admin/initiatives/${slug}/intersections`}
            className="shrink-0 text-sm font-medium text-accent-600 hover:text-accent-500"
          >
            View intersections →
          </Link>
        </div>
        <p className="mt-1 text-sm text-slate-500">
          Reorder the queue and control which prompt members see. One prompt can be active per
          initiative at a time.
        </p>

        <label className="mt-3 inline-flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={state.initiative.allow_edits}
            disabled={acting}
            onChange={(e) => setAllowEdits(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-accent-600 focus:ring-accent-500 disabled:opacity-50"
          />
          Allow members to edit a submitted answer while the prompt is active
        </label>
      </div>

      {actionError && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          {actionError}
        </div>
      )}

      <section>
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-2">
          Currently active
        </h3>
        {(state.active_activations?.length ?? 0) > 0 ? (
          <div className="space-y-2">
            {state.active_activations.map((activation, idx) => (
              <div key={activation.id} className="rounded-md border border-emerald-200 bg-emerald-50 p-4">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 shrink-0 text-xs font-semibold text-emerald-700">
                    {idx + 1}.
                  </span>
                  <p className="text-sm text-slate-900">{activation.prompt.question_text}</p>
                </div>
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    disabled={acting}
                    onClick={() => finalizeActive(activation.id)}
                    className="rounded-md bg-emerald-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
                  >
                    Finalize
                  </button>
                  <button
                    type="button"
                    disabled={acting}
                    onClick={() => skipActive(activation.id)}
                    className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    Skip
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-md border border-slate-200 bg-white p-4 text-sm text-slate-600">
            <p>No active prompts. Activate queued prompts to start collecting responses.</p>
          </div>
        )}
        {/* Multiple prompts can be live at once — members step through them
            in queue order. This activates the next not-yet-activated
            queued prompt. */}
        <button
          type="button"
          disabled={acting || state.queue.length === 0}
          onClick={activateNext}
          className="mt-3 rounded-md bg-accent-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-500 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          Activate next queued prompt
        </button>
      </section>

      <section>
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-2">Queue</h3>
        {state.queue.length === 0 ? (
          <p className="text-sm text-slate-500">No prompts queued yet. Add one below.</p>
        ) : (
          <ol className="space-y-2">
            {state.queue.map((entry, idx) => (
              <li
                key={entry.entry_id}
                draggable={!acting}
                onDragStart={() => setDragIndex(idx)}
                onDragOver={(e) => {
                  e.preventDefault() // allow drop
                  if (overIndex !== idx) setOverIndex(idx)
                }}
                onDrop={() => {
                  if (dragIndex !== null) reorder(dragIndex, idx)
                  setDragIndex(null)
                  setOverIndex(null)
                }}
                onDragEnd={() => {
                  setDragIndex(null)
                  setOverIndex(null)
                }}
                className={`flex items-start justify-between gap-3 rounded-md border bg-white p-3 transition ${
                  dragIndex === idx ? 'opacity-50' : ''
                } ${
                  overIndex === idx && dragIndex !== null && dragIndex !== idx
                    ? 'border-accent-400 ring-1 ring-accent-300'
                    : 'border-slate-200'
                }`}
              >
                <div className="min-w-0 flex items-start gap-3">
                  <span
                    aria-hidden="true"
                    title="Drag to reorder"
                    className={`mt-0.5 select-none text-slate-400 ${acting ? 'cursor-not-allowed' : 'cursor-grab'}`}
                  >
                    ⠿
                  </span>
                  <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-medium text-slate-600">
                    {idx + 1}
                  </span>
                  <p className="text-sm text-slate-800">{entry.prompt.question_text}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => move(idx, -1)}
                    disabled={acting || idx === 0}
                    aria-label="Move up"
                    className="rounded-md px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 disabled:opacity-30"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => move(idx, +1)}
                    disabled={acting || idx === state.queue.length - 1}
                    aria-label="Move down"
                    className="rounded-md px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 disabled:opacity-30"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() => removePrompt(entry.prompt.id)}
                    disabled={acting}
                    className="rounded-md px-2 py-1 text-xs text-rose-600 hover:bg-rose-50 disabled:opacity-30"
                  >
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>

      <section>
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-2">
          Add a prompt
        </h3>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search prompts by question text…"
          className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
        />
        {results.length > 0 && (
          <ul className="mt-2 max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-white">
            {results.map((p) => (
              <li
                key={p.id}
                className="flex items-start justify-between gap-3 border-b border-slate-100 p-3 last:border-b-0"
              >
                <p className="text-sm text-slate-700">{p.question_text}</p>
                <button
                  type="button"
                  onClick={() => addPrompt(p.id)}
                  disabled={acting}
                  className="shrink-0 rounded-md bg-accent-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-accent-500 disabled:opacity-50"
                >
                  Add
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div>
        <Link to="/" className="text-sm text-slate-500 hover:text-slate-700">
          ← Back to Home
        </Link>
      </div>
    </div>
  )
}
